import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, useNavigate } from 'react-router-dom'

import { GoogleReCaptcha } from '../../lib/components'
import { CBLogoV2 } from '../../lib/components/Icon'
import {
  getPartnerHomePath,
  hasActivePartnerSession,
  partnerLoginRequiresRecaptcha,
  setPartnerSession,
} from '../../lib/utils/partner-session'
import { getPartnerErrorMessage, partnerServices } from '../../services'

import '../login-v2/login-v2.scss'
import './partner.scss'

const PartnerLoginContainer = () => {
  const navigate = useNavigate()
  const reRef = useRef<ReCAPTCHA>(null)
  const requiresRecaptcha = partnerLoginRequiresRecaptcha()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (hasActivePartnerSession()) {
      navigate(getPartnerHomePath(), { replace: true })
      return
    }
    setReady(true)
  }, [navigate])

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault()
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail || !password) {
      setError('Enter your email and password.')
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      let recaptchaToken: string | undefined
      if (requiresRecaptcha) {
        recaptchaToken = (await reRef.current?.executeAsync()) || undefined
        reRef.current?.reset()
        if (!recaptchaToken) {
          setError('Complete the security check and try again.')
          return
        }
      }

      const data = await partnerServices.login(normalizedEmail, password, recaptchaToken)
      setPartnerSession(data.authToken, {
        userId: data.userId,
        name: data.name,
        email: data.email,
        role: data.role,
        referralCode: data.referralCode,
        passwordResetUsed: data.passwordResetUsed === true,
      })
      navigate(data.passwordResetUsed ? '/partner/investors' : '/partner/set-password', {
        replace: true,
      })
    } catch (loginError) {
      setError(
        getPartnerErrorMessage(loginError, 'Unable to log in. Check your email and password.'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!ready) return null

  const canSubmit = email.trim() && password
  const buttonClass = isSubmitting
    ? 'login-page__btn-primary login-page__btn-primary--loading'
    : canSubmit
    ? 'login-page__btn-primary login-page__btn-primary--enabled'
    : 'login-page__btn-primary login-page__btn-primary--disabled'

  return (
    <div className='login-page partner-login' data-testid='partner-login-page'>
      <aside className='login-page__brand' aria-label='Credibila partner portal'>
        <header className='login-page__brand-header'>
          <h1 className='login-page__brand-header-title'>Partner Portal</h1>
        </header>
        <div
          className='login-page__brand-hero'
          role='img'
          aria-label='Client reviewing markets on a tablet'
        />
        <section className='login-page__brand-stats' aria-label='Key statistics'>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>6+ Countries</div>
            <div className='login-page__stat-label'>Global Presence</div>
          </div>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>3000+</div>
            <div className='login-page__stat-label'>Strong Community</div>
          </div>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>80+ Years</div>
            <div className='login-page__stat-label'>Combined Team Experience</div>
          </div>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>$25+ Million</div>
            <div className='login-page__stat-label'>AUM</div>
          </div>
        </section>
      </aside>
      <main className='login-page__form-section' aria-label='Partner login'>
        <div className='login-page__form-card'>
          <Link to='/home' className='login-page__form-logo' aria-label='Go to home'>
            <CBLogoV2 classes={['login-page__form-logo-svg']} width='168' height='40' />
          </Link>
          <h2 className='partner-login__title'>Login</h2>
          <p className='partner-login__subtitle'>
            Use the email and temporary password from Credibila.
          </p>

          {error ? (
            <p className='partner-login__error' role='alert'>
              {error}
            </p>
          ) : null}

          <form className='partner-login__form' onSubmit={handleLogin} noValidate>
            <div className='partner-login__field'>
              <label htmlFor='partner-email'>Email</label>
              <input
                id='partner-email'
                name='email'
                type='email'
                autoComplete='username'
                autoCapitalize='none'
                spellCheck={false}
                value={email}
                onChange={(event) => setEmail(event.target.value.toLowerCase())}
              />
            </div>
            <div className='partner-login__field'>
              <label htmlFor='partner-password'>Password</label>
              <input
                id='partner-password'
                name='password'
                type='password'
                autoComplete='current-password'
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            {requiresRecaptcha ? (
              <GoogleReCaptcha
                handleChange={() => undefined}
                reCaptchaRef={reRef}
                size='invisible'
              />
            ) : null}
            <button type='submit' className={buttonClass} disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}

export { PartnerLoginContainer }
